import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Link2, Unlink2, RefreshCw, ArrowLeft } from "lucide-react";

const PROVIDER_LABELS = {
  parasut: "Paraşüt",
  bizimhesap: "BizimHesap",
  woocommerce: "WooCommerce",
  trendyol: "Trendyol",
  hepsiburada: "Hepsiburada",
  amazon: "Amazon",
  n11: "N11",
  idefix: "idefix",
  pazarama: "Pazarama",
  shopify: "Shopify",
  ikas: "ikas",
  shopier: "Shopier",
};

export default function IntegrationProductMatchingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [items, setItems] = useState([]);
  const [integration, setIntegration] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [total, setTotal] = useState(0);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerTotalPages, setPickerTotalPages] = useState(1);
  const [savingLink, setSavingLink] = useState(false);
  const [n11Categories, setN11Categories] = useState([]);
  const [n11CategoriesLoading, setN11CategoriesLoading] = useState(false);
  const [selectedN11CategoryKey, setSelectedN11CategoryKey] = useState("");
  const [accountingProvider, setAccountingProvider] = useState("parasut");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [selectedWarehouseName, setSelectedWarehouseName] = useState("");

  const provider = String(integration?.provider || "").toLowerCase();
  const isParasutProvider = provider === "parasut";
  const isBizimhesapProvider = provider === "bizimhesap";
  // Product matching must follow the selected integration provider, not global accounting preference.
  const isAccountingByBizimhesap = isBizimhesapProvider;
  const providerLabel = PROVIDER_LABELS[provider] || provider || "Entegrasyon";
  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / perPage)), [total, perPage]);
  const isN11Provider = provider === "n11";

  const getN11CategoryText = useCallback((obj) => {
    const details = (((obj?.channel_details || {}).n11) || obj || {});
    const path = String(details?.category_path || "").trim();
    const name = String(details?.category_name || "").trim();
    const id = String(details?.category_id || "").trim();
    return path || name || id || "";
  }, []);

  const getProviderLinkValue = useCallback((product, providerKey) => {
    if (!product) return "";
    const key = String(providerKey || "").toLowerCase();
    if (key === "woocommerce") {
      return String((product?.channel_links || {}).woocommerce || product?.wc_id || "").trim();
    }
    return String((product?.channel_links || {})[key] || "").trim();
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const intg = await api.get(`/integrations/${id}`);
      const invoiceSettings = (intg?.credentials?.invoice_settings || {});
      setIntegration({
        id: intg?.id,
        provider: intg?.provider,
        name: intg?.name,
        selected_warehouse_id: invoiceSettings?.selected_warehouse_id || "",
        selected_warehouse_name: invoiceSettings?.selected_warehouse_name || "",
      });

      const allProducts = await api.get("/products");
      const rows = Array.isArray(allProducts) ? allProducts : [];
      const q = String(search || "").trim().toLowerCase();
      const filtered = rows.filter((p) => {
        if (!q) return true;
        const name = String(p?.name || "").toLowerCase();
        const sku = String(p?.sku || "").toLowerCase();
        const barcode = String(p?.barcode || "").toLowerCase();
        return name.includes(q) || sku.includes(q) || barcode.includes(q);
      });
      filtered.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "tr"));

      const start = (Math.max(1, page) - 1) * perPage;
      const paged = filtered.slice(start, start + perPage).map((p) => ({
        ...p,
        integration_external_id:
          String(intg?.provider || "").toLowerCase() === "parasut"
            ? String(p?.parasut_product_id || (p?.channel_links || {}).parasut || "").trim()
            : isAccountingByBizimhesap
              ? String((p?.channel_links || {}).bizimhesap || "").trim()
              : getProviderLinkValue(p, intg?.provider),
      }));

      setItems(paged);
      setTotal(filtered.length);
    } catch (error) {
      toast.error(error.message || "Ürün listesi alınamadı");
    } finally {
      setLoading(false);
    }
  }, [id, page, perPage, search, getProviderLinkValue, isAccountingByBizimhesap]);

  const fetchAccountingProvider = useCallback(async () => {
    try {
      const cfg = await api.get("/settings/accounting");
      const p = String(cfg?.provider || "").toLowerCase();
      if (p === "bizimhesap" || p === "parasut") setAccountingProvider(p);
      else if (cfg?.bizimhesap?.connected) setAccountingProvider("bizimhesap");
      else if (cfg?.parasut?.connected) setAccountingProvider("parasut");
      else setAccountingProvider("parasut");
    } catch {
      setAccountingProvider("parasut");
    }
  }, []);

  const fetchProductSettingsWarehouse = useCallback(async () => {
    try {
      const cfg = await api.get("/settings/product-settings");
      const wid = String(cfg?.selected_warehouse_id || "").trim();
      const wname = String(cfg?.selected_warehouse_name || "").trim();
      setSelectedWarehouseId(wid);
      setSelectedWarehouseName(wname);
    } catch {
      setSelectedWarehouseId("");
      setSelectedWarehouseName("");
    }
  }, []);

  useEffect(() => {
    fetchAccountingProvider();
    fetchProductSettingsWarehouse();
  }, [fetchAccountingProvider, fetchProductSettingsWarehouse]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchParasutProducts = useCallback(async (query = "", p = 1) => {
    setPickerLoading(true);
    try {
      const warehousePart = integration?.selected_warehouse_id
        ? `&warehouse_id=${encodeURIComponent(String(integration.selected_warehouse_id))}`
        : "";
      const res = await api.get(`/parasut/products?search=${encodeURIComponent(query)}&page=${p}&per_page=25${warehousePart}`);
      const next = Array.isArray(res?.items) ? res.items : [];
      setPickerItems(p === 1 ? next : (prev) => [...prev, ...next]);
      setPickerPage(Number(res?.page || p));
      setPickerTotalPages(Number(res?.total_pages || 1));
    } catch (error) {
      toast.error(error.message || "Paraşüt ürünleri alınamadı");
    } finally {
      setPickerLoading(false);
    }
  }, [integration?.selected_warehouse_id]);

  const fetchBizimhesapProducts = useCallback(async (query = "", p = 1) => {
    setPickerLoading(true);
    try {
      const q = String(query || "").trim().toLowerCase();
      const perPageLocal = 25;
      const hasSelectedWarehouse = !!selectedWarehouseId;
      let rows = [];

      if (hasSelectedWarehouse) {
        const res = await api.get(`/bizimhesap/inventory/${encodeURIComponent(selectedWarehouseId)}`);
        rows = Array.isArray(res?.items) ? res.items : [];
      } else {
        const res = await api.get(`/bizimhesap/products?search=${encodeURIComponent(query || "")}&page=1&per_page=2000`);
        rows = Array.isArray(res?.items) ? res.items : [];
      }

      const normalized = rows
        .map((it) => {
          const idVal = String(
            it?.id ?? it?.product_id ?? it?.guid ?? it?.code ?? it?.sku ?? it?.barcode ?? ""
          ).trim();
          const nameVal = String(it?.name ?? it?.title ?? it?.product_name ?? "").trim();
          const skuVal = String(it?.code ?? it?.sku ?? it?.stock_code ?? "").trim();
          const barcodeVal = String(it?.barcode ?? it?.ean ?? it?.gtin ?? it?.upc ?? "").trim();
          return {
            id: idVal || skuVal || barcodeVal,
            external_id: idVal || skuVal || barcodeVal,
            name: nameVal || `BizimHesap #${idVal || skuVal || "Ürün"}`,
            sku: skuVal,
            barcode: barcodeVal,
          };
        })
        .filter((it) => it.id)
        .filter((it) => {
          if (!q) return true;
          return (
            String(it?.name || "").toLowerCase().includes(q) ||
            String(it?.sku || "").toLowerCase().includes(q) ||
            String(it?.barcode || "").toLowerCase().includes(q) ||
            String(it?.external_id || "").toLowerCase().includes(q)
          );
        });

      const totalCount = normalized.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / perPageLocal));
      const safePage = Math.max(1, Math.min(p, totalPages));
      const start = (safePage - 1) * perPageLocal;
      const pageItems = normalized.slice(start, start + perPageLocal);

      setPickerItems(pageItems);
      setPickerPage(safePage);
      setPickerTotalPages(totalPages);
    } catch (error) {
      toast.error(error.message || "BizimHesap ürünleri alınamadı");
    } finally {
      setPickerLoading(false);
    }
  }, [selectedWarehouseId]);

  const fetchSourceProducts = useCallback(async (query = "", p = 1) => {
    setPickerLoading(true);
    try {
      const res = await api.get(
        `/integrations/${id}/source-products?search=${encodeURIComponent(query)}&page=${p}&per_page=25`
      );
      const next = Array.isArray(res?.items) ? res.items : [];
      setPickerItems(p === 1 ? next : (prev) => [...prev, ...next]);
      setPickerPage(Number(res?.page || p));
      const totalCount = Number(res?.total || next.length);
      const pages = Math.max(1, Math.ceil(totalCount / 25));
      setPickerTotalPages(pages);
      if (res?.warning) {
        toast.warning(String(res.warning));
      }
    } catch (error) {
      toast.error(error.message || "Entegrasyon ürünleri alınamadı");
    } finally {
      setPickerLoading(false);
    }
  }, [id]);

  const fetchN11Categories = useCallback(async (query = "", p = 1) => {
    if (!isN11Provider) return;
    setN11CategoriesLoading(true);
    try {
      const res = await api.get(
        `/n11/${id}/categories?search=${encodeURIComponent(query || "")}&page=${p}&per_page=250`
      );
      const rows = Array.isArray(res?.items) ? res.items : [];
      setN11Categories(rows);
    } catch (error) {
      toast.error(error.message || "N11 kategorileri alınamadı");
      setN11Categories([]);
    } finally {
      setN11CategoriesLoading(false);
    }
  }, [id, isN11Provider]);

  const openPicker = (product) => {
    setActiveProduct(product);
    setPickerSearch("");
    setPickerItems([]);
    setPickerPage(1);
    setPickerTotalPages(1);
    const existingN11 = ((product?.channel_details || {}).n11) || {};
    setSelectedN11CategoryKey(
      isN11Provider ? `${existingN11?.category_id || ""}|${existingN11?.category_name || ""}` : ""
    );
    setPickerOpen(true);
    if (isN11Provider) {
      fetchN11Categories("", 1);
    }
    if (isAccountingByBizimhesap) {
      fetchBizimhesapProducts("", 1);
    } else if (isParasutProvider) {
      fetchParasutProducts("", 1);
    } else {
      fetchSourceProducts("", 1);
    }
  };

  const handleLink = async (pickedItem) => {
    if (!activeProduct?.id) return;
    setSavingLink(true);
    try {
      if (isAccountingByBizimhesap) {
        await api.post(`/products/${activeProduct.id}/link-channel`, {
          provider: "bizimhesap",
          external_id: String(pickedItem?.external_id || pickedItem?.id || "").trim(),
          source_product_id: String(pickedItem?.id || "").trim(),
        });
      } else if (isParasutProvider) {
        await api.post(
          `/products/${activeProduct.id}/link-parasut?parasut_product_id=${encodeURIComponent(pickedItem.id)}&parasut_product_name=${encodeURIComponent(pickedItem.name || "")}`
        );
      } else {
        const selectedN11Category = n11Categories.find(
          (x) => `${x?.category_id || ""}|${x?.category_name || ""}` === selectedN11CategoryKey
        );
        const pickedN11Category = {
          category_id: String(
            selectedN11Category?.category_id || pickedItem?.category_id || ""
          ).trim(),
          category_name: String(
            selectedN11Category?.category_name || pickedItem?.category_name || ""
          ).trim(),
          category_path: String(
            selectedN11Category?.category_path || pickedItem?.category_path || ""
          ).trim(),
        };
        if (isN11Provider && !pickedN11Category.category_name && !pickedN11Category.category_id) {
          toast.error("N11 için kategori seçimi gerekli.");
          return;
        }
        await api.post(`/products/${activeProduct.id}/link-channel`, {
          provider,
          external_id: String(pickedItem?.external_id || pickedItem?.id || "").trim(),
          source_product_id: String(pickedItem?.id || "").trim(),
          category_id: pickedN11Category.category_id,
          category_name: pickedN11Category.category_name,
          category_path: pickedN11Category.category_path,
        });
      }
      toast.success("Ürün eşleştirildi");
      setPickerOpen(false);
      await fetchProducts();
    } catch (error) {
      toast.error(error.message || "Eşleştirme yapılamadı");
    } finally {
      setSavingLink(false);
    }
  };

  const handleUnlink = async (productId) => {
    try {
      if (isAccountingByBizimhesap) {
        await api.post(`/products/${productId}/unlink-channel`, { provider: "bizimhesap" });
      } else if (isParasutProvider) {
        await api.post(`/products/${productId}/unlink-parasut`);
      } else {
        await api.post(`/products/${productId}/unlink-channel`, { provider });
      }
      toast.success("Eşleştirme kaldırıldı");
      await fetchProducts();
    } catch (error) {
      toast.error(error.message || "Eşleştirme kaldırılamadı");
    }
  };

  const handleSyncStoreProducts = async () => {
    if (provider !== "woocommerce" && provider !== "n11" && provider !== "pazarama") {
      toast.info("Bu entegrasyonda ürün çekme bu ekranda desteklenmiyor.");
      return;
    }
    setSyncing(true);
    try {
      const res =
        provider === "woocommerce"
          ? await api.post(`/woocommerce/${id}/sync-products`)
          : provider === "n11"
            ? await api.post(`/n11/${id}/sync-products`)
            : await api.get(`/pazarama/products?integration_id=${encodeURIComponent(id)}&page=1&page_size=25`);
      toast.success(res?.message || "Mağaza ürünleri çekildi");
      await fetchProducts();
    } catch (error) {
      toast.error(error.message || "Mağaza ürünleri çekilemedi");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="page-shell space-y-4">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/panel/entegrasyonlar")}
            className="h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted/50 inline-flex items-center justify-center transition-colors shrink-0"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          <div className="app-page-header">
            <h1 className="app-page-title">Ürün Eşleştirme</h1>
            <p className="app-page-subtitle">
              {integration?.name || "-"} / {providerLabel || "-"}
              {isParasutProvider && integration?.selected_warehouse_id ? ` · Depo: ${integration?.selected_warehouse_name || integration?.selected_warehouse_id}` : ""}
              {isAccountingByBizimhesap && selectedWarehouseId ? ` · BizimHesap Depo: ${selectedWarehouseName || selectedWarehouseId}` : ""}
            </p>
          </div>
        </div>
        <div className="page-header-actions">
          <Button variant="outline" onClick={fetchProducts} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Yenile
          </Button>
          <Button onClick={handleSyncStoreProducts} disabled={syncing}>
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Mağaza Ürünlerini Çek
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-lg border bg-background px-3 h-10">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          className="flex-1 bg-transparent text-sm outline-none"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Ürün adı, stok kodu veya barkod ile ara..."
        />
      </div>

      <div className="table-shell overflow-hidden">
        <div className="sheet-heading">
          <div>
            <h2>Ürün Eşleştirme</h2>
            {!loading && <p>{total} kayıt</p>}
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="px-5 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground" style={{width:"42%"}}>Ürün</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground" style={{width:"25%"}}>{providerLabel} Kodu</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground" style={{width:"17%"}}>{providerLabel} Eşleşme</th>
              <th className="px-5 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground" style={{width:"16%"}}>İşlem</th>
            </tr>
          </thead>
          <tbody>

          {loading ? (
            <tr><td colSpan={4} className="px-5 py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={4} className="px-5 py-10 text-center text-sm text-muted-foreground">Eşleştirilecek ürün bulunamadı.</td></tr>
          ) : (
            items.map((p) => {
              const mappedExternal = isParasutProvider
                ? String(p?.parasut_product_id || (p?.channel_links || {}).parasut || "").trim()
                : (
                  isAccountingByBizimhesap
                    ? String((p?.channel_links || {}).bizimhesap || "").trim()
                    : getProviderLinkValue(p, provider)
                );
              return (
                <tr key={p.id} className="border-b hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-3 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground">SKU: {p.sku || "-"} / Barkod: {p.barcode || "-"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground font-mono">
                    <div className="truncate">{mappedExternal || "-"}</div>
                    {isN11Provider ? (
                      <div className="truncate text-[11px] text-muted-foreground">
                        Kategori: {getN11CategoryText(p) || "-"}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {isParasutProvider ? (
                      mappedExternal ? (
                        <span className="text-emerald-700">{p.parasut_product_name || mappedExternal}</span>
                      ) : (
                        <span className="text-amber-700">Eşleşmedi</span>
                      )
                    ) : (
                      mappedExternal ? <span className="text-emerald-700">Eşleşti</span> : <span className="text-amber-700">Eşleşmedi</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {mappedExternal ? (
                        <Button variant="outline" size="sm" onClick={() => handleUnlink(p.id)}>
                          <Unlink2 className="h-3.5 w-3.5 mr-1" />
                          Kaldır
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => openPicker(p)}>
                          <Link2 className="h-3.5 w-3.5 mr-1" />
                          Eşleştir
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
          </tbody>
        </table>
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between text-sm text-muted-foreground">
          <span>{total} kayıt · Sayfa {page} / {totalPages}</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)}>Önceki</Button>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((prev) => prev + 1)}>Sonraki</Button>
          </div>
        </div>
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isAccountingByBizimhesap
                ? "BizimHesap Ürünü Seç"
                : isParasutProvider
                  ? "Paraşüt Ürünü Seç"
                  : `${providerLabel} Ürünü Seç`}
            </DialogTitle>
            <DialogDescription>{activeProduct?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Ürün adı veya kod ile ara..."
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  if (isAccountingByBizimhesap) fetchBizimhesapProducts(pickerSearch, 1);
                  else if (isParasutProvider) fetchParasutProducts(pickerSearch, 1);
                  else fetchSourceProducts(pickerSearch, 1);
                }}
              />
              <Button
                variant="outline"
                onClick={() => {
                  if (isAccountingByBizimhesap) fetchBizimhesapProducts(pickerSearch, 1);
                  else if (isParasutProvider) fetchParasutProducts(pickerSearch, 1);
                  else fetchSourceProducts(pickerSearch, 1);
                }}
                disabled={pickerLoading}
              >
                {pickerLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {isN11Provider ? (
              <div className="rounded-md border p-2">
                <p className="mb-1 text-xs text-muted-foreground">N11 Kategori Seçimi</p>
                <Select value={selectedN11CategoryKey} onValueChange={setSelectedN11CategoryKey}>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={n11CategoriesLoading ? "Kategoriler yükleniyor..." : "Kategori seçin (önerilen)"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {n11Categories.map((c) => {
                      const key = `${c?.category_id || ""}|${c?.category_name || ""}`;
                      const label = c?.category_path || c?.category_name || c?.category_id || "Kategori";
                      return (
                        <SelectItem key={key || label} value={key}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="max-h-[360px] overflow-auto rounded-md border divide-y">
              {pickerItems.length === 0 && !pickerLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Sonuç bulunamadı.</div>
              ) : (
                pickerItems.map((pp) => (
                  <button
                    key={pp.id}
                    type="button"
                    className="w-full text-left p-3 hover:bg-muted/50 disabled:opacity-60"
                    onClick={() => handleLink(pp)}
                    disabled={savingLink}
                  >
                    <div className="text-sm font-medium">{pp.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Kod: {((isParasutProvider && !isAccountingByBizimhesap) ? pp.code : (pp.external_id || pp.id)) || "-"} / Barkod: {pp.barcode || "-"}
                    </div>
                    {isN11Provider ? (
                      <div className="text-xs text-muted-foreground">
                        Kategori: {pp?.category_path || pp?.category_name || pp?.category_id || "-"}
                      </div>
                    ) : null}
                  </button>
                ))
              )}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">{pickerPage} / {pickerTotalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={pickerLoading || pickerPage >= pickerTotalPages}
                onClick={() => {
                  if (isAccountingByBizimhesap) fetchBizimhesapProducts(pickerSearch, pickerPage + 1);
                  else if (isParasutProvider) fetchParasutProducts(pickerSearch, pickerPage + 1);
                  else fetchSourceProducts(pickerSearch, pickerPage + 1);
                }}
              >
                Daha Fazla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
